import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Factory, ShieldCheck } from "lucide-react";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { categories } from "@/data/categories";
import { useStartProject } from "@/lib/startProjectContext";

// Small handpicked cluster + capability hint for each industry so tiles feel
// specific, not generic. Uses only content that already lives elsewhere on the
// site (no invented claims).
const HINTS = {
  lighting: { clusters: "Moradabad · Firozabad", moq: "500 units · 35-day lead" },
  furniture: { clusters: "Jodhpur · Saharanpur · Panipat", moq: "200 units · 60-day lead" },
  "home-decor": { clusters: "Jaipur · Agra · Noida · Moradabad", moq: "300 units · 45-day lead" },
  textile: { clusters: "Panipat · Karur · Jaipur", moq: "500 units · 50-day lead" },
  "brass-metalware": { clusters: "Moradabad · Aligarh · Jagadhri", moq: "500 units · 40-day lead" },
  "fashion-accessories": { clusters: "Jaipur · Delhi · Moradabad", moq: "300 units · 40-day lead" },
};

const TRUST = [
  {
    icon: Factory,
    title: "1,000+ vetted factories",
    body: "Personally audited across 15 manufacturing clusters — social compliance verified, IP-safe, English-fluent management.",
  },
  {
    icon: MapPin,
    title: "15 manufacturing clusters",
    body: "From Moradabad brass to Jodhpur furniture, Jaipur crafts to Panipat textile — deep vertical presence where it matters.",
  },
  {
    icon: ShieldCheck,
    title: "AQL 2.5 + BSCI · Sedex",
    body: "Every container inspected: in-line, mid-line, final random. Full documentation and photograph archive delivered to buyer.",
  },
];

const IndustriesHero = () => {
  const { open } = useStartProject();
  return (
    <section className="ind-hero" data-testid="industries-hero">
      <div className="ind-hero-veil" aria-hidden />
      <div className="ind-hero-inner wrap">
        <p className="ind-eyebrow">Industries we source</p>
        <h1 className="ind-h1">
          Six verticals. <br /> Fifteen clusters. <br /> One accountable team.
        </h1>
        <p className="ind-lede">
          Asia Sourcing manages India-side manufacturing for premium retailers, hospitality
          operators and DTC brands across six commercial verticals — each backed by decades of
          on-the-floor experience in the clusters that produce them.
        </p>
        <div className="ind-hero-actions">
          <button type="button" className="ql-cta-primary" onClick={open} data-testid="industries-start-project">
            Start Your Project <ArrowRight size={16} strokeWidth={1.6} aria-hidden />
          </button>
        </div>
      </div>
    </section>
  );
};

const IndustryTile = ({ cat, ix }) => {
  const hint = HINTS[cat.id] || {};
  return (
    <Link
      to={`/categories/${cat.id}`}
      className="ind-tile"
      data-testid={`industry-${cat.id}`}
    >
      <div className="ind-tile-media" aria-hidden>
        <img src={cat.image} alt="" loading="lazy" />
        <div className="ind-tile-veil" />
      </div>
      <div className="ind-tile-body">
        <span className="ind-tile-ix">/ {String(ix + 1).padStart(2, "0")}</span>
        <h2 className="ind-tile-h2">{cat.name}</h2>
        <p className="ind-tile-tag">{cat.tagline}</p>
        <ul className="ind-tile-meta">
          {hint.clusters && (
            <li>
              <MapPin size={13} strokeWidth={1.4} aria-hidden />
              <span>{hint.clusters}</span>
            </li>
          )}
          {hint.moq && (
            <li>
              <Factory size={13} strokeWidth={1.4} aria-hidden />
              <span>{hint.moq}</span>
            </li>
          )}
        </ul>
        <span className="ind-tile-cta">
          Explore {cat.name.toLowerCase()} <ArrowRight size={13} strokeWidth={1.6} aria-hidden />
        </span>
      </div>
    </Link>
  );
};

export const Industries = () => {
  useSeo({
    title: "Industries · India sourcing across six verticals · Asia Sourcing",
    description:
      "Lighting, furniture, home décor, textile, brass and fashion accessories — India-side manufacturing across 15 clusters. 1,000+ vetted factories. AQL 2.5 inspection on every container.",
    path: "/industries",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/industries#page`,
          name: "Industries — India sourcing across six verticals",
          url: `${SITE_URL}/industries`,
          hasPart: categories.map((c) => ({
            "@type": "WebPage",
            name: `${c.name} sourcing in India`,
            url: `${SITE_URL}/categories/${c.id}`,
          })),
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Industries", path: "/industries" },
        ]),
      ],
    },
  });

  return (
    <article data-testid="industries-page">
      <IndustriesHero />

      {/* Grid of industries */}
      <section className="ind-grid-section">
        <div className="wrap">
          <div className="ind-grid">
            {categories.map((cat, ix) => (
              <IndustryTile key={cat.id} cat={cat} ix={ix} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust row */}
      <section className="ind-trust-section">
        <div className="wrap">
          <p className="ind-trust-eyebrow">How we operate</p>
          <div className="ind-trust-grid">
            {TRUST.map((t) => (
              <div key={t.title} className="ind-trust-tile">
                <t.icon size={22} strokeWidth={1.3} aria-hidden />
                <h3>{t.title}</h3>
                <p>{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
};

export default Industries;
